/* eslint-disable camelcase */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push as pushAction } from 'react-router-redux';
import utils from '@utils';
import { Row, Col, Grid } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import { InvoiceEstimatesService, PaymentService, InvoiceService } from '@services';
import { NavBar } from '@components';
import { Loader, SubNavBar } from '@components/common';
import Actions, { thunks } from '@redux/app';
import {
	EstimateSummary,
	InfoLabel,
	Expired,
	PaymentConfirmationModal,
	PaymentPreviewModal,
	Header,
} from './components';
import InvoiceInfo from './InvoiceInfo';
import DeclineEstimate from './DeclineEstimate';
import Final from './Final';

class InvoiceEstimate extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			step             : 'info', // values: info, decline, final, expired
			invoiceEstimate  : {},
			loading          : false,
			declineReason    : '',
			reasonValidation : null,
			approve          : false,
			errorMessage     : null,
			receiptId        : null,
			goBackToJobs     : false,
		};

		const { match : { params : { hash } } } = props;
		// check for tokenized IDs which contain instance of 0 at the first index, 'hash[0]'
		this.anonymous = Number.isNaN(Number(hash)) || hash[0] === '0'; // Do not use isNaN from lodash
	}

	componentDidMount() {
		const { location : { state }, match : { params : { hash } } } = this.props;
		this.getInvoice(hash);
		if (state && state.fromLogin) {
			this.setState({ goBackToJobs : true });
		}
	}

	fetchPaymentMethods = (force = false) => {
		const { isRkPay, getPaymentMethods } = this.props;
		if (force || isRkPay) getPaymentMethods();
	}

	showAddPaymentModal = () => {
		const { setField } = this.props;

		setField('addPayment', 'showAddPayment', true);
	}

	getInvoice = (hash) => {
		const { push } = this.props;
		this.setState({ loading : true });

		let endpoint = 'getInvoiceEstimateFromId';
		if (this.anonymous) endpoint = 'getInvoiceEstimateFromToken';

		InvoiceEstimatesService[endpoint](hash)
			.then(({ data : invoiceEstimate }) => {
				const { RequiresPrepayment, SentTo } = invoiceEstimate;
				const { isAuthenticated } = this.props;

				/** Conditions to navigate to the prepay signup page:
				 * - User must not be signed in to an account already
				 * - Estimate should require prepayment
				 * - SentTo must have some value (email address to populate on signup page)
				 */
				if (!isAuthenticated && RequiresPrepayment && SentTo) {
					push(`/prepay/signup/${hash}`, { email : SentTo, validInvoice : true });
				} else {
					this.setState({ invoiceEstimate, loading : false });
				}
			})
			.catch(({ response }) => {
				if (response.status === 400) this.setState({ step : 'expired', loading : false, errorMessage : response.data.Message });
				else if (response.status === 401 && !this.anonymous) push(`/login?estId=${hash}`);
				else window.location.href = '/404';
			});
	}

	getInvoiceEstimatePDF = () => {
		const { match : { params : { hash } } } = this.props;

		this.setState({ loading : true });

		let endpoint = 'getInvoiceEstimatePDFFromId';
		if (this.anonymous) endpoint = 'getInvoiceEstimatePDFFromToken';

		InvoiceEstimatesService[endpoint](hash)
			.then(({ data }) => {
				this.setState({ loading : false });
				utils.downloadReportFromBlobResponse(data, `rollkall_invoice_estimate_${hash}.pdf`);
			})
			.catch(() => {
				this.setState({ loading : false });
				utils.notify({
					type    : 'error',
					message : 'Error loading invoice estimate',
				});
			});
	}

	getEstimateReceiptPDF = () => {
		const { receiptId } = this.state;

		if (receiptId === null) {
			utils.notify({
				type    : 'error',
				message : 'Receipt cannot be generated',
			});
			return;
		}

		const { match : { params : { hash } } } = this.props;
		this.setState({ loading : true });

		InvoiceService.downloadReceiptPDF({ id : receiptId })
			.then(({ data }) => {
				this.setState({ loading : false, step : 'final', approve : true });
				utils.downloadReportFromBlobResponse(data, `rollkall_estimate_receipt_${hash}.pdf`);
			})
			.catch(() => {
				this.setState({ loading : false, step : 'final', approve : true });
				utils.notify({
					type    : 'error',
					message : 'Error loading estimate receipt',
				});
			});
	}

	openPaymentConfirmationModal = (total) => {
		this._paymentConfirmationModal.openModal({ total });
	}

	openPaymentPreviewModal = (total, defaultCard, showBillingMessage, paymentMethods) => {
		this._paymentPreviewModal.openModal({
			total, defaultCard, showBillingMessage, paymentMethods,
		});
	}

	redirectToFinal = (approve = true) => {
		this.setState({ step : 'final', loading : false, approve });
	}

	onClickApprove = (approve) => () => {
		if (approve) {
			const { invoiceEstimate } = this.state;

			if (invoiceEstimate.RequiresPrepayment) {
				// To approve first we check existing payment methods
				// If there are no payment cards available, the payment modal will open
				this.setState({ loading : true });
				PaymentService.getData().then(({ data }) => {
					this.setState({ loading : false }, () => {
						if (data && ((data.PaymentCards && data.PaymentCards.length > 0) || (data.BankAccounts && data.BankAccounts.length > 0))) {
							const bankAccounts = (data.BankAccounts || []).map((account) => ({
								id            : account.BankAccountId,
								type          : 'bank_account',
								isDefault     : account.IsDefaultCard,
								bankName      : account.BankName,
								accountNumber : account.AccountNumber.substr(account.AccountNumber.lastIndexOf('*') + 1),
								accountStatus : account.BankAccountStatus,
							}));
							const cards = (data.PaymentCards || []).map((card) => ({
								id         : card.PaymentCardId,
								type       : 'card',
								isDefault  : card.IsDefaultCard,
								cardNumber : card.CardNumber.replace(/[.]/g, '*'),
								cardType   : card.CardType.replace(' ', '-').toLowerCase(),
								expiry     : `${card.ExpirationMonth.toString().length === 1
									? `0${card.ExpirationMonth}`
									: card.ExpirationMonth}/${card.ExpirationYear.toString().substr(2)}`,
							}));
							const paymentMethods = [...bankAccounts, ...cards];
							const defaultCard = paymentMethods.find((item) => item.isDefault);
							const { InitialChargeAmount, JobEventRecurrenceRuleId, WeeklyTotal } = invoiceEstimate;

							this.openPaymentPreviewModal(
								InitialChargeAmount,
								defaultCard,
								JobEventRecurrenceRuleId && (InitialChargeAmount > WeeklyTotal),
								paymentMethods,
							);
						} else {
							this.showAddPaymentModal();
						}
					});
				});
			} else {
				this.approveEstimate(true);
			}
			return;
		}

		const { declineReason } = this.state;

		// Validate decline reason
		if (!approve && declineReason.length === 0) {
			this.setState({ reasonValidation : 'error' });
			return;
		}

		// Decline estimate
		this.approveEstimate(false);
	}

	approveEstimate = (approve) => {
		const { match : { params : { hash } } } = this.props;
		const { invoiceEstimate, declineReason } = this.state;

		const queryParams = {
			name : invoiceEstimate.ClientContactName,
			approve,
		};

		const data = {};
		if (!approve) data.note = declineReason;

		this.setState({ loading : true });

		let endpoint = 'approveEstimateFromId';
		if (this.anonymous) endpoint = 'approveEstimateFromToken';

		InvoiceEstimatesService[endpoint](hash, queryParams, data)
			.then(({ data : result }) => {
				if (approve) {
					if (invoiceEstimate.RequiresPrepayment) {
						this.setState({ receiptId : result.PaymentReceiptId }, () => {
							this.openPaymentConfirmationModal(invoiceEstimate.InitialChargeAmount);
						});
					} else {
						this.redirectToFinal(true);
					}
				} else {
					this.redirectToFinal(false);
				}
			})
			.catch((error) => {
				this.setState({ loading : false });

				const { response } = error;
				utils.notify({
					type    : 'error',
					message : response.data.Message || 'Error processing invoice estimate',
				});
			});
	}

	handleChangeDeclineReason = (event, reasonPreset) => {
		const declineReason = event ? event.target.value : reasonPreset;
		this.setState({ declineReason });
	}

	changeStep = (step) => this.setState({ step });

	TitleSection = () => {
		const { invoiceEstimate, approve, step } = this.state;
		const { user : { TimeZoneId } } = this.props;
		const approvedTitle = invoiceEstimate.RequiresPrepayment ? 'Payment Complete' : 'Estimate Approved';
		if (step === 'final') {
			return (
				<Grid className="padding-top-30 padding-bottom-30">
					<div className="font-size-24 padding-bottom-10 font-semi-bold">
						{approve ? approvedTitle : 'Estimate Declined'}
					</div>
				</Grid>
			);
		}
		return (
			<Grid className="padding-top-30 padding-bottom-30">
				<div className="font-size-24 padding-bottom-10 font-semi-bold">
					{invoiceEstimate.RequiresPrepayment ? 'Review & Pay' : 'Approve Estimate'}
				</div>
				<div className="font-size-12">
					{ `Sent by: ${invoiceEstimate.AgencyContactEmail}, ${utils.parseUTCDateToTimeZone(invoiceEstimate.CreateDateUTC, TimeZoneId)
						.format('MMMM DD, YYYY')}` }
				</div>
			</Grid>
		);
	}

	render() {
		const {
			step, invoiceEstimate, loading, declineReason, reasonValidation, approve, errorMessage, goBackToJobs,
		} = this.state;

		const {
			isAuthenticated,
			user,
			user : { TimeZoneId }, push,
			location : { pathname },
			history  : { goBack },
		} = this.props;

		if (isEmpty(invoiceEstimate) && step !== 'expired') return <Loader active />;

		return (
			<div id="invoice-estimate-body">
				<Choose>
					<When condition={isAuthenticated}>
						<NavBar acive={pathname} />
					</When>
					<Otherwise>
						<Header />
					</Otherwise>
				</Choose>
				{isAuthenticated && <SubNavBar onClickBack={goBackToJobs ? () => push('/jobs/list/needApproval') : goBack} />}
				<If condition={step !== 'expired'}>
					<this.TitleSection />
				</If>
				<Grid className="position-relative">
					<Choose>
						<When condition={step === 'expired'}>
							<Expired message={errorMessage} />
						</When>
						<When condition={step === 'final'}>
							<Final
								JobEventStartDateTime={invoiceEstimate.JobEventStartDateTime}
								timeZoneId={TimeZoneId}
								approved={approve}
							/>
						</When>
						<Otherwise>
							<Row id="invoice-estimate-container">
								<Col
									md={8}
									sm={12}
									className="padding-40 padding-left-xs-20 padding-right-xs-20"
								>
									<InfoLabel
										isPrepay={invoiceEstimate.RequiresPrepayment}
										contactName={invoiceEstimate.AgencyContactName}
									/>
									<Choose>
										<When condition={step === 'info'}>
											<InvoiceInfo
												timeZoneId={TimeZoneId}
												invoiceEstimate={invoiceEstimate}
												getInvoiceEstimatePDF={this.getInvoiceEstimatePDF}
											/>
										</When>
										<When condition={step === 'decline'}>
											<DeclineEstimate
												reason={declineReason}
												reasonValidation={reasonValidation}
												onChange={this.handleChangeDeclineReason}
												declineEstimate={this.onClickApprove(false)}
											/>
										</When>
									</Choose>
								</Col>
								<Col
									md={4}
									sm={12}
									className="padding-left-0 padding-right-0"
								>
									<EstimateSummary
										invoiceEstimate={invoiceEstimate.InvoiceEstimate}
										isRecurring={invoiceEstimate.JobEventRecurrenceRepeatType !== null}
										step={step}
										loading={loading}
										changeStep={this.changeStep}
										approveEstimate={this.onClickApprove(true)}
										clearNote={() => this.handleChangeDeclineReason(null, '')}
										isPrepay={invoiceEstimate.RequiresPrepayment}
										isPaid={invoiceEstimate.PaymentReceiptId !== null}
									/>
								</Col>
							</Row>
						</Otherwise>
					</Choose>
					<Loader active={loading} />
				</Grid>
				<PaymentConfirmationModal
					ref={(ref) => { this._paymentConfirmationModal = ref; }}
					onPrintReceipt={this.getEstimateReceiptPDF}
					onDone={this.redirectToFinal}
				/>
				<PaymentPreviewModal
					user={user}
					callback={this.fetchPaymentMethods}
					ref={(ref) => { this._paymentPreviewModal = ref; }}
					onPay={() => this.approveEstimate(true)}
					achPrePayEnabled={invoiceEstimate.AchPrePayEnabled}
					showAddPaymentModal={this.showAddPaymentModal}
				/>
			</div>
		);
	}
}

const mapStateToProps = ({
	app      : {
		isAuthenticated,
		user,
		user : { IsRKPay : isRkPay },
	},
}) => ({
	isAuthenticated,
	user,
	isRkPay,
});

const mapDispatchToProps = (dispatch) => ({
	push              : bindActionCreators(pushAction, dispatch),
	getPaymentMethods : bindActionCreators(thunks, dispatch).getPaymentMethods,
	setField          : bindActionCreators(Actions, dispatch).setFormField,
});

InvoiceEstimate.propTypes = {
	user              : PropTypes.object.isRequired,
	isAuthenticated   : PropTypes.bool,
	push              : PropTypes.func.isRequired,
	match             : PropTypes.object.isRequired,
	location          : PropTypes.object.isRequired,
	history           : PropTypes.object.isRequired,
	getPaymentMethods : PropTypes.func.isRequired,
	setField          : PropTypes.func.isRequired,
	isRkPay           : PropTypes.bool,
};

InvoiceEstimate.defaultProps = {
	isAuthenticated : false,
	isRkPay         : false,
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceEstimate);
